import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StyledSelect } from '@/components/ui/styled-select';
import { Users, TrendingUp, DollarSign, Activity, Plus, X, Phone, Mail, Building2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEAL_STAGES = [
  { id: 'lead', label: 'Lead', color: 'bg-blue-500' },
  { id: 'viewing', label: 'Viewing', color: 'bg-yellow-500' },
  { id: 'negotiation', label: 'Negotiation', color: 'bg-orange-500' },
  { id: 'closed', label: 'Closed', color: 'bg-emerald-500' }
];

const AgentCRMPage = () => {
  const [deals, setDeals] = useState([]);
  const [pipeline, setPipeline] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newDeal, setNewDeal] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    property_id: '',
    deal_value: 5000000,
    commission_amount: 150000,
    stage: 'lead',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dealsRes, pipelineRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/agent/deals`),
        axios.get(`${API}/agent/pipeline`),
        axios.get(`${API}/agent/analytics`)
      ]);
      setDeals(dealsRes.data);
      setPipeline(pipelineRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error('Failed to load CRM data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDeal = async () => {
    try {
      await axios.post(`${API}/agent/deals`, newDeal);
      toast.success('Deal created successfully!');
      setShowAddModal(false);
      setNewDeal({
        client_name: '',
        client_email: '',
        client_phone: '',
        property_id: '',
        deal_value: 5000000,
        commission_amount: 150000,
        stage: 'lead',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create deal');
    }
  };

  const handleUpdateStage = async (dealId, newStage) => {
    try {
      await axios.put(`${API}/agent/deals/${dealId}`, { stage: newStage });
      toast.success('Deal stage updated!');
      fetchData();
    } catch (error) {
      toast.error('Failed to update deal');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', minimumFractionDigits: 0 }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-gold text-xl">Loading CRM...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian text-text-primary">
      <nav className="glass-card border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-[1440px] mx-auto px-8 py-6 flex justify-between items-center">
          <Link to="/dashboard" className="font-playfair text-3xl font-semibold text-gold">ASTIIV BASGAT</Link>
          <div className="flex items-center gap-6">
            <Link to="/dashboard" className="text-text-secondary hover:text-gold transition-colors">Dashboard</Link>
            <Link to="/analytics" className="text-text-secondary hover:text-gold transition-colors">Analytics</Link>
            <Link to="/investor" className="text-text-secondary hover:text-gold transition-colors">Investor</Link>
            <Link to="/developer" className="text-text-secondary hover:text-gold transition-colors">Developer</Link>
            <Link to="/agent" className="text-gold">Agent</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-[1440px] mx-auto px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex justify-between items-start mb-12">
          <div>
            <h1 className="font-playfair text-5xl font-semibold mb-4">Agent CRM</h1>
            <p className="text-text-secondary text-lg">Manage leads, track deals, and optimize your sales pipeline</p>
          </div>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-gold text-black hover:bg-gold-light"
            data-testid="add-deal-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Deal
          </Button>
        </motion.div>

        {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-8" data-testid="total-deals-card">
            <Users className="w-8 h-8 text-gold mb-4" />
            <div className="text-3xl font-bold mb-2">{analytics?.total_deals || 0}</div>
            <div className="text-text-secondary">Total Deals</div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass-card p-8" data-testid="active-deals-card">
            <Activity className="w-8 h-8 text-gold mb-4" />
            <div className="text-3xl font-bold mb-2">{analytics?.active_deals || 0}</div>
            <div className="text-text-secondary">Active Deals</div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="glass-card p-8" data-testid="close-rate-card">
            <TrendingUp className="w-8 h-8 text-gold mb-4" />
            <div className="text-3xl font-bold mb-2">{analytics?.close_rate || 0}%</div>
            <div className="text-text-secondary">Close Rate</div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="glass-card p-8" data-testid="total-commission-card">
            <DollarSign className="w-8 h-8 text-gold mb-4" />
            <div className="text-3xl font-bold mb-2">{formatCurrency(analytics?.total_commission || 0)}</div>
            <div className="text-text-secondary">Total Commission</div>
          </motion.div>
        </div>

        {/* Deal Pipeline */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <h2 className="font-playfair text-3xl font-semibold mb-6">Deal Pipeline</h2>
          
          {deals.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <Users className="w-16 h-16 text-gold mx-auto mb-6 opacity-50" />
              <h3 className="font-playfair text-2xl font-semibold mb-4">No Deals Yet</h3>
              <p className="text-text-secondary text-lg mb-6">Start tracking your real estate deals</p>
              <Button onClick={() => setShowAddModal(true)} className="bg-gold text-black hover:bg-gold-light">
                <Plus className="w-4 h-4 mr-2" /> Add Your First Deal
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {DEAL_STAGES.map((stage) => (
                <div key={stage.id} className="space-y-4" data-testid={`pipeline-${stage.id}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-3 h-3 rounded-full ${stage.color}`} />
                    <h3 className="font-semibold text-lg">{stage.label}</h3>
                    <span className="text-text-secondary text-sm">({(pipeline[stage.id] || []).length})</span>
                  </div>
                  
                  <div className="space-y-3 min-h-[200px]">
                    {(pipeline[stage.id] || []).map((deal, index) => (
                      <motion.div
                        key={deal.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="glass-card p-4 border border-white/10 hover:border-gold/30 transition-all"
                        data-testid={`deal-card-${deal.id}`}
                      >
                        <h4 className="font-semibold mb-2">{deal.client_name}</h4>
                        <div className="text-sm text-text-secondary space-y-1">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-3 h-3" />
                            {formatCurrency(deal.deal_value)}
                          </div>
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-3 h-3 text-emerald-400" />
                            <span className="text-emerald-400">{formatCurrency(deal.commission_amount)}</span>
                          </div>
                        </div>
                        
                        {stage.id !== 'closed' && (
                          <div className="mt-3 pt-3 border-t border-white/10">
                            <StyledSelect
                              value={deal.stage}
                              onChange={(v) => handleUpdateStage(deal.id, v)}
                              options={DEAL_STAGES.map((s) => ({ value: s.id, label: s.label }))}
                              className="h-8 text-sm"
                              data-testid={`stage-select-${deal.id}`}
                            />
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Add Deal Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-card p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-playfair text-2xl font-semibold">Add New Deal</h2>
              <Button variant="ghost" onClick={() => setShowAddModal(false)} className="text-text-secondary hover:text-white">
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-text-primary mb-2 block">Client Name</Label>
                <Input
                  value={newDeal.client_name}
                  onChange={(e) => setNewDeal({ ...newDeal, client_name: e.target.value })}
                  className="bg-white text-black border-white/20 focus:border-gold"
                  placeholder="e.g., Ahmed Al Maktoum"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-text-primary mb-2 block">Email</Label>
                  <Input
                    type="email"
                    value={newDeal.client_email}
                    onChange={(e) => setNewDeal({ ...newDeal, client_email: e.target.value })}
                    className="bg-white text-black border-white/20 focus:border-gold"
                    placeholder="client@email.com"
                  />
                </div>
                <div>
                  <Label className="text-text-primary mb-2 block">Phone</Label>
                  <Input
                    value={newDeal.client_phone}
                    onChange={(e) => setNewDeal({ ...newDeal, client_phone: e.target.value })}
                    className="bg-white text-black border-white/20 focus:border-gold"
                    placeholder="+971..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-text-primary mb-2 block">Deal Value (AED)</Label>
                  <Input
                    type="number"
                    value={newDeal.deal_value}
                    onChange={(e) => setNewDeal({ ...newDeal, deal_value: parseFloat(e.target.value) })}
                    className="bg-white text-black border-white/20 focus:border-gold"
                  />
                </div>
                <div>
                  <Label className="text-text-primary mb-2 block">Commission (AED)</Label>
                  <Input
                    type="number"
                    value={newDeal.commission_amount}
                    onChange={(e) => setNewDeal({ ...newDeal, commission_amount: parseFloat(e.target.value) })}
                    className="bg-white text-black border-white/20 focus:border-gold"
                  />
                </div>
              </div>

              <div>
                <Label className="text-text-primary mb-2 block">Stage</Label>
                <StyledSelect
                  value={newDeal.stage}
                  onChange={(v) => setNewDeal({ ...newDeal, stage: v })}
                  options={DEAL_STAGES.map((s) => ({ value: s.id, label: s.label }))}
                  data-testid="new-deal-stage-select"
                />
              </div>

              <div>
                <Label className="text-text-primary mb-2 block">Notes</Label>
                <textarea
                  value={newDeal.notes}
                  onChange={(e) => setNewDeal({ ...newDeal, notes: e.target.value })}
                  className="w-full bg-white text-black border border-white/20 focus:border-gold rounded-md p-3 min-h-[100px]"
                  placeholder="Additional notes..."
                />
              </div>

              <Button
                onClick={handleCreateDeal}
                disabled={!newDeal.client_name || !newDeal.client_email}
                className="w-full bg-gold text-black hover:bg-gold-light mt-4"
                data-testid="submit-deal-btn"
              >
                Create Deal
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AgentCRMPage;
